<script lang="ts" setup>
import {
  PhArrowDown,
  PhArrowUp,
  PhCircleNotch,
  PhDownloadSimple,
  PhEnvelope,
  PhFloppyDisk,
  PhPencilSimple,
  PhPhone,
  PhPlus,
  PhTrash,
  PhUsersThree,
  PhX,
} from '@phosphor-icons/vue';
import { useI18n } from 'vue-i18n';
import { useProjectOrganisationExternalLogic } from './logic/useProjectOrganisationExternalLogic';
import type {
  ProjectOrganisationExternalActionEvent,
  UseProjectOrganisationExternalLogicProps,
} from './types';

const props = defineProps<UseProjectOrganisationExternalLogicProps>();
const emit = defineEmits<{
  action: [event: ProjectOrganisationExternalActionEvent];
}>();

const { t } = useI18n();
const { state, action } = useProjectOrganisationExternalLogic(props, (event) =>
  emit('action', event),
);
</script>

<template>
  <div class="card">
    <div class="card-header">
      <p class="card-header-title">
        <PhUsersThree :size="18" class="mr-2" />
        {{ state.organisation.name }}
      </p>
      <button
        v-if="state.permissions.downloadButton === 'visible'"
        class="download-btn"
        :title="t('overview.projectOrganisations.downloadCsv')"
        :disabled="state.organisation.stakeholders.length === 0"
        @click="action.downloadCsv()"
      >
        <PhDownloadSimple :size="15" />
        CSV
      </button>
      <button
        v-if="state.permissions.cancelButton === 'visible'"
        class="cancel-toggle"
        @click="action.cancel()"
      >
        <PhX :size="15" />
        {{ t('cancel') }}
      </button>
      <button
        v-if="state.permissions.editToggle === 'visible'"
        class="edit-toggle"
        :class="{
          'is-active': state.mode === 'edit' || state.mode === 'saving',
        }"
        :disabled="
          state.mode === 'saving' ||
          (state.mode === 'edit' && !state.hasChanges)
        "
        @click="action.toggleEdit()"
      >
        <PhCircleNotch v-if="state.mode === 'saving'" :size="15" class="spin" />
        <PhFloppyDisk v-else-if="state.mode === 'edit'" :size="15" />
        <PhPencilSimple v-else :size="15" />
        {{
          state.mode === 'edit' || state.mode === 'saving'
            ? t('save')
            : t('overview.projectMetaData.editButton')
        }}
      </button>
    </div>
    <div class="card-content p-0">
      <div
        v-if="state.organisation.stakeholders.length === 0"
        class="empty-state"
      >
        <PhUsersThree :size="32" />
        <span>{{ t('overview.projectOrganisations.emptyState') }}</span>
      </div>
      <ul v-else class="stakeholder-list">
        <li
          v-for="stakeholder in state.organisation.stakeholders"
          :key="stakeholder.id"
          class="stakeholder-item"
          :class="{ 'is-editing': state.permissions.moveButtons === 'visible' }"
        >
          <div
            v-if="state.permissions.moveButtons === 'visible'"
            class="move-btns"
          >
            <button class="move-btn" @click="action.moveUp(stakeholder.id)">
              <PhArrowUp :size="12" />
            </button>
            <button class="move-btn" @click="action.moveDown(stakeholder.id)">
              <PhArrowDown :size="12" />
            </button>
          </div>
          <div class="stakeholder-avatar">
            {{ stakeholder.name?.charAt(0) }}
          </div>
          <div class="stakeholder-info">
            <div class="stakeholder-top">
              <template v-if="state.permissions.stakeholderInputs === 'edit'">
                <input
                  class="inline-input role-input"
                  :value="stakeholder.role"
                  @change="
                    action.update(
                      stakeholder.id,
                      'role',
                      ($event.target as HTMLInputElement).value,
                    )
                  "
                />
                <input
                  class="inline-input name-input"
                  :value="stakeholder.name"
                  @change="
                    action.update(
                      stakeholder.id,
                      'name',
                      ($event.target as HTMLInputElement).value,
                    )
                  "
                />
              </template>
              <template v-else>
                <span class="stakeholder-role">{{ stakeholder.role }}</span>
                <span class="stakeholder-name">{{ stakeholder.name }}</span>
              </template>
            </div>
            <div class="stakeholder-contact">
              <span class="contact-item">
                <template v-if="state.permissions.stakeholderInputs === 'edit'">
                  <span class="input-pill">
                    <span class="pill-icon"><PhPhone :size="14" /></span>
                    <input
                      class="pill-input"
                      :value="stakeholder.phone"
                      @change="
                        action.update(
                          stakeholder.id,
                          'phone',
                          ($event.target as HTMLInputElement).value,
                        )
                      "
                    />
                  </span>
                </template>
                <template v-else>
                  <PhPhone :size="14" />
                  <a :href="`tel:${stakeholder.phone}`" class="contact-link">{{
                    stakeholder.phone
                  }}</a>
                </template>
              </span>
              <span class="contact-item">
                <template v-if="state.permissions.stakeholderInputs === 'edit'">
                  <span class="input-pill">
                    <span class="pill-icon"><PhEnvelope :size="14" /></span>
                    <input
                      class="pill-input"
                      :value="stakeholder.email"
                      @change="
                        action.update(
                          stakeholder.id,
                          'email',
                          ($event.target as HTMLInputElement).value,
                        )
                      "
                    />
                  </span>
                </template>
                <template v-else>
                  <PhEnvelope :size="14" />
                  <a
                    :href="`mailto:${stakeholder.email}`"
                    class="contact-link"
                    >{{ stakeholder.email }}</a
                  >
                </template>
              </span>
            </div>
          </div>
          <Transition name="fade-delete">
            <button
              v-if="state.permissions.deleteButton === 'visible'"
              class="delete-btn"
              @click="action.delete(stakeholder.id)"
            >
              <PhTrash :size="16" />
            </button>
          </Transition>
        </li>
      </ul>
      <Transition name="fade-delete">
        <div
          v-if="state.permissions.addRow === 'visible'"
          class="stakeholder-item add-row"
        >
          <div class="stakeholder-info">
            <div class="stakeholder-top">
              <input
                v-model="state.newEntry.role"
                class="add-input role-input"
                :placeholder="t('role')"
              />
              <input
                v-model="state.newEntry.name"
                class="add-input name-input"
                :placeholder="t('name')"
              />
            </div>
            <div class="stakeholder-contact">
              <span class="contact-item">
                <span class="input-pill">
                  <span class="pill-icon"><PhPhone :size="14" /></span>
                  <input
                    v-model="state.newEntry.phone"
                    class="pill-input"
                    :placeholder="t('phone')"
                  />
                </span>
              </span>
              <span class="contact-item">
                <span class="input-pill">
                  <span class="pill-icon"><PhEnvelope :size="14" /></span>
                  <input
                    v-model="state.newEntry.email"
                    class="pill-input"
                    :placeholder="t('email')"
                    @keydown.enter="action.submitNew()"
                  />
                </span>
              </span>
            </div>
          </div>
          <button class="add-btn" @click="action.submitNew()">
            <PhPlus :size="14" />
            {{ t('add') }}
          </button>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.card {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 22rem;
}

.card-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 1;
}

.edit-toggle {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  align-self: center;
  height: auto;
  width: auto;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: #f5f5f7;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s,
    opacity 0.15s;
}

.edit-toggle.is-active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.edit-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.download-btn {
  margin-right: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  align-self: center;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: #f5f5f7;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.download-btn:hover {
  background: color-mix(in srgb, var(--primary-color) 10%, white);
  border-color: color-mix(in srgb, var(--primary-color) 40%, white);
  color: var(--primary-color);
}

.download-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cancel-toggle {
  margin-right: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  align-self: center;
  height: auto;
  width: auto;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: #f5f5f7;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
}

.cancel-toggle:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.inline-input {
  background: #f0f0f3;
  border: none;
  border-radius: 999px;
  outline: none;
  padding: 0 0.85rem;
  height: var(--bulma-control-height);
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  min-width: 0;
  transition: background 0.15s;
}

.inline-input:focus {
  background: #e8e8ed;
}

.inline-input::placeholder {
  color: #bbb;
}

.add-row {
  background: color-mix(in srgb, var(--primary-color) 5%, white);
  flex-shrink: 0;
}

.add-input {
  background: #fff;
  border: none;
  border-radius: 999px;
  outline: none;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  padding: 0 0.85rem;
  height: var(--bulma-control-height);
  min-width: 0;
  transition: background 0.15s;
}

.add-input:focus {
  background: #f8f8fa;
}

.add-input::placeholder {
  color: #bbb;
}

.role-input {
  font-size: 0.9rem;
  font-weight: 600;
  color: #222;
  flex: 1;
}

.name-input {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #777;
  margin-left: auto;
  margin-right: 1.5rem;
  text-align: right;
  width: 11rem;
  flex-shrink: 0;
}

.add-row .role-input {
  flex: 1;
}

.add-row .name-input {
  margin-left: auto;
  margin-right: 6.5rem;
  padding-left: 0.75rem;
  align-self: center;
}

.contact-input {
  font-size: 0.8rem;
  color: #666;
}

.input-pill {
  display: flex;
  align-items: center;
  background: #f0f0f3;
  border-radius: 999px;
  height: var(--bulma-control-height);
  padding: 0 0.65rem 0 0.25rem;
  gap: 0.3rem;
  transition: background 0.15s;
}

.input-pill:focus-within {
  background: #e8e8ed;
}

.add-row .input-pill {
  background: #fff;
}

.add-row .input-pill:focus-within {
  background: #f8f8fa;
}

.contact-item:last-child .input-pill {
  min-width: 13rem;
}

.pill-icon {
  flex-shrink: 0;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.pill-input {
  background: transparent;
  border: none;
  outline: none;
  flex: 1;
  min-width: 0;
  font-family: inherit;
  font-size: 0.8rem;
  color: #666;
}

.pill-input::placeholder {
  color: #bbb;
}

.add-btn {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  justify-content: center;
  height: 1.75rem;
  padding: 0 0.75rem;
  border: none;
  border-radius: 999px;
  background: var(--primary-color);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.15s;
}

.add-btn:hover {
  opacity: 0.85;
}

.stakeholder-item.is-editing {
  padding-left: 2.5rem;
  padding-right: 2.75rem;
}

.move-btns {
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: absolute;
  left: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  opacity: 1;
  transition: opacity 0.15s;
  z-index: 1;
}

.move-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #555;
  cursor: pointer;
  transition: color 0.15s;
}

.move-btn:hover {
  color: #333;
}

.delete-btn {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: none;
  border-radius: 50%;
  background: #fee2e2;
  color: #dc2626;
  cursor: pointer;
  transition: background 0.15s;
  opacity: 1;
}

.delete-btn:hover {
  background: #fca5a5;
}

.fade-delete-enter-active,
.fade-delete-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}

.fade-delete-enter-from,
.fade-delete-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

.stakeholder-list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.stakeholder-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
}

.stakeholder-item:last-child {
  border-bottom: none;
}

.stakeholder-avatar {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: var(--primary-color);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
}

.stakeholder-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
  flex: 1;
}

.stakeholder-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stakeholder-role {
  font-size: 0.9rem;
  font-weight: 600;
  color: #222;
}

.stakeholder-name {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #777;
  margin-left: auto;
  white-space: nowrap;
}

.stakeholder-contact {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #666;
}

.contact-link {
  color: #666;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
  color: var(--primary-color);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2.5rem 2rem;
  color: #aaa;
  font-size: 0.9rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.spin {
  animation: spin 0.8s linear infinite;
}
</style>
